import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Calendar, CheckCircle, XCircle, Clock, Camera,
  ChevronDown, ChevronUp
} from "lucide-react";

const RiderAttendance = ({ user, onLogout }) => {
  const { riderId } = useParams();
  const navigate = useNavigate();
  
  const [rider, setRider] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDates, setExpandedDates] = useState({});

  // Fetch rider details and attendance
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [riderRes, attendanceRes] = await Promise.all([
          axios.get(`${API}/pharmeasy-orders/riders/${riderId}`),
          axios.get(`${API}/pharmeasy-orders/riders/${riderId}/attendance-history`)
        ]);
        setRider(riderRes.data);
        setAttendanceHistory(attendanceRes.data.attendance || []);
      } catch (e) {
        toast.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [riderId]);

  const formatTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { timeStyle: "short" }); }
    catch { return iso; }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return "—";
    try { 
      return new Date(dateStr).toLocaleDateString("en-IN", { 
        weekday: "long", 
        day: "numeric", 
        month: "long", 
        year: "numeric" 
      }); 
    }
    catch { return dateStr; }
  };

  const toggleExpanded = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  // Calculate summary stats
  const totalDays = attendanceHistory.length;
  const completedDays = attendanceHistory.filter(a => a.check_in && a.check_out).length;
  const totalHours = attendanceHistory.reduce((sum, a) => sum + (a.total_hours || 0), 0);
  const avgHours = completedDays > 0 ? (totalHours / completedDays).toFixed(1) : 0;

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-slate-600">Loading...</div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="rider-attendance-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/pharmeasy/riders")} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Rider Attendance</h1>
            <p className="text-slate-600">{rider?.name} • {rider?.mobile}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md bg-blue-50">
            <CardContent className="pt-4">
              <p className="text-xs text-blue-600 uppercase font-medium">Total Days</p>
              <p className="text-3xl font-bold text-blue-800">{totalDays}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-green-50">
            <CardContent className="pt-4">
              <p className="text-xs text-green-600 uppercase font-medium">Completed Days</p>
              <p className="text-3xl font-bold text-green-800">{completedDays}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-purple-50">
            <CardContent className="pt-4">
              <p className="text-xs text-purple-600 uppercase font-medium">Total Hours</p>
              <p className="text-3xl font-bold text-purple-800">{totalHours.toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-amber-50">
            <CardContent className="pt-4">
              <p className="text-xs text-amber-600 uppercase font-medium">Avg Hours/Day</p>
              <p className="text-3xl font-bold text-amber-800">{avgHours}</p>
            </CardContent>
          </Card>
        </div>

        {/* Attendance Records */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-5">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-500" />
              Attendance History (Last 30 Days)
            </h2>

            {attendanceHistory.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg text-slate-500">No attendance records found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attendanceHistory.map((record, idx) => (
                  <div key={idx} className={`border rounded-lg overflow-hidden ${record.check_out ? "border-slate-200" : "border-green-300 bg-green-50/30"}`}>
                    {/* Header Row */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleExpanded(record.date)}
                    >
                      <div className="flex items-center gap-4">
                        {expandedDates[record.date] ? 
                          <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        }
                        <div>
                          <p className="font-medium text-slate-800">{formatDateFull(record.date)}</p>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              In: {formatTime(record.check_in)}
                            </span>
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Out: {formatTime(record.check_out)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {record.total_hours && (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Duration</p>
                            <p className="font-medium text-slate-700">{record.total_hours.toFixed(2)} hrs</p>
                          </div>
                        )}
                        <Badge className={record.check_out ? "bg-slate-100 text-slate-600" : "bg-green-100 text-green-700"}>
                          {record.check_out ? "Completed" : "Active"}
                        </Badge>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedDates[record.date] && (
                      <div className="border-t bg-slate-50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Check In Details */}
                          <div className="bg-white rounded-lg p-4 border border-green-200">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-green-700">Check In</p>
                                <p className="text-2xl font-bold text-green-800">{formatTime(record.check_in)}</p>
                              </div>
                            </div>
                            {record.check_in_selfie && (
                              <a 
                                href={record.check_in_selfie} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm mt-2"
                              >
                                <Camera className="w-4 h-4" />
                                View Check-In Selfie
                              </a>
                            )}
                            {record.check_in_location && (
                              <div className="mt-2 text-xs text-slate-500">
                                Location: {record.check_in_location.latitude?.toFixed(4)}, {record.check_in_location.longitude?.toFixed(4)}
                              </div>
                            )}
                          </div>

                          {/* Check Out Details */}
                          <div className="bg-white rounded-lg p-4 border border-red-200">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                <XCircle className="w-4 h-4 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-red-700">Check Out</p>
                                <p className="text-2xl font-bold text-red-800">{record.check_out ? formatTime(record.check_out) : "—"}</p>
                              </div>
                            </div>
                            {record.check_out_selfie && (
                              <a 
                                href={record.check_out_selfie} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm mt-2"
                              >
                                <Camera className="w-4 h-4" />
                                View Check-Out Selfie
                              </a>
                            )}
                            {record.check_out_location && (
                              <div className="mt-2 text-xs text-slate-500">
                                Location: {record.check_out_location.latitude?.toFixed(4)}, {record.check_out_location.longitude?.toFixed(4)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Odometer Reading */}
                        {(record.check_in_odometer || record.check_out_odometer) && (
                          <div className="mt-4 bg-white rounded-lg p-4 border">
                            <p className="text-sm font-medium text-slate-600 mb-2">Odometer Readings</p>
                            <div className="flex items-center gap-8">
                              <div>
                                <span className="text-xs text-slate-500">Start:</span>
                                <span className="ml-2 font-mono">{record.check_in_odometer || "—"} km</span>
                              </div>
                              <div>
                                <span className="text-xs text-slate-500">End:</span>
                                <span className="ml-2 font-mono">{record.check_out_odometer || "—"} km</span>
                              </div>
                              {record.check_in_odometer && record.check_out_odometer && (
                                <div>
                                  <span className="text-xs text-slate-500">Distance:</span>
                                  <span className="ml-2 font-mono font-medium text-blue-600">
                                    {(record.check_out_odometer - record.check_in_odometer).toFixed(1)} km
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default RiderAttendance;
